import type { Amenity } from "@/types/Amenity";
import * as LucideIcons from "lucide-react";

/**
 * Renders an amenity icon and name using Lucide icons.
 *
 * @param {AmenityIconRendererProps} props - The props containing the amenity.
 * @returns {JSX.Element} The rendered icon and name.
 *
 * @example
 * <AmenityIconRenderer amenity={{ id: '1', name: 'WiFi', icon: 'Wifi' }} />
 */
type AmenityIconRendererProps = {
  amenity: Amenity;
  hideLabel?: boolean;
  withDescription?: boolean;
};

/**
 * Renders an amenity icon and name using Lucide icons.
 *
 * @param {AmenityIconRendererProps} props - The props containing the amenity.
 * @returns {JSX.Element} The rendered icon and name.
 *
 * @example
 * <AmenityIconRenderer amenity={{ id: '1', name: 'WiFi', icon: 'Wifi' }} />
 */
const AmenityIconRenderer = ({ amenity, hideLabel = false, withDescription = false }: AmenityIconRendererProps) => {
  // Validate icon string and get Lucide icon component
  const IconComponent = amenity.icon && (LucideIcons as unknown as Record<string, React.FC<any>>)[amenity.icon];

  if (!amenity || !amenity.name) {
    // Early return for invalid input
    return null;
  }

  return (
    <div className="flex items-center" title={amenity.name}>
      {IconComponent ? (
        <IconComponent className="min-w-5 w-5 h-5 mr-2 text-gray-700" aria-label={amenity.name} />
      ) : (
        <span className="w-5 h-5 mr-2 inline-block bg-gray-200 rounded" aria-hidden="true" />
      )}
      {!hideLabel && (
        <span className="text-sm text-gray-700">
          {typeof amenity.count === "number" && amenity.count > 0 && <>{amenity.count}x </>}
          {amenity.name}
          {withDescription && amenity.description && (
            <span className="block text-xs text-gray-500">{amenity.description}</span>
          )}
        </span>
      )}
    </div>
  );
};

export default AmenityIconRenderer;
